import request from '@utils/axiosWrapper';
import qs from 'qs';
import * as Ids from '@utils/ids';

/** 
 * 广告二维码打印内容
 * 
 * 示例：
 * ```json
{
    "copyList": ["文案测试", "文案测试兜底"],
    "copyFont": "D",
    "adUrl": "http://lingbo.waimai.test.sankuai.com/ad/game/monopoly?source_id=6&poiid=689568#/",
    "isCopyAbove": true
}
```
 */
type AdPrintContent = {
    [index: string]: any;
};

type OrderPrintInfo = {
    [index: string]: any;
};

/** 订单里单个商品的标签打印信息 */
type OrderFoodTagPrintContent = {
    [index: string]: any;
};

/** 存餐码打印数据 */
type DepositCodeInfo = {
    /** 公司地址 */
    recipientAddress: string;
    /** 餐柜信息 */
    cupboard: string;
    /** 收餐时间 */
    estimatedDeliveryTime: string;
    /** 存餐码数据 */
    depositCode: string;
};

/**
 * 获取订单小票广告二维码内容
 *
 * @param orderPoiId 订单所属门店ID
 */
const getAdPrintContent = (orderPoiId: number) =>
    request.rawPostAsFormSubmit<{ code: number; data: AdPrintContent }>('/gw/api/printer/ad/get', {
        appId: 1,
        appType: 3,
        wmPoiId: orderPoiId,
    });

/**
 * 获取门店订单小票打印配置（**返回的是一个JSON字符串**)
 * 返回的数据格式参考 小票打印配置example.json
 *
 * @param orderPoiId 订单所属门店ID
 */
const getReceiptPrintConfig = (orderPoiId: number) => {
    return request.rawPostAsFormSubmit<{ code: string; data: string; msg: string }>(
        '/api/printer/template/original/config/get',
        {
            appId: 1,
            receiptHolders: JSON.stringify([1, 2, 3, 4]),
            wmPoiId: orderPoiId,
        },
    );
};

type OrderPrintInfoQueryParams = {
    wmOrderViewId: string;
    wmPoiId: number | string;
    getNewVo: number;
    templateConfig: string | null;
};

/**
 * 获取普通订单打印信息
 */
const getOrderPrintInfo = (params: OrderPrintInfoQueryParams) => {
    return request.rawPost<{ code: number; msg: string; data: OrderPrintInfo[] }>(
        `/v2/order/history/r/print/info/v2?${qs.stringify(
            { ...params, token: Ids.getToken(), acctId: Ids.getAcctId() },
            { skipNulls: true },
        )}`,
    );
};

/**
 * 获取拼好饭单买打印信息
 * @param params
 */
const getPhfSingleOrderPrintInfo = (params: OrderPrintInfoQueryParams) => {
    return request.rawPost<{ code: number; msg: string; data: OrderPrintInfo[] }>(
        `/gw/phf/v2/order/history/r/print/info/v2?${qs.stringify(
            { ...params, token: Ids.getToken(), acctId: Ids.getAcctId() },
            { skipNulls: true },
        )}`,
    );
};

/**
 * 获取拼好饭拼单合单打印详情
 */
const getPhfCombineOrderPrintInfo = (params: OrderPrintInfoQueryParams) => {
    return request.rawPost<{ code: number; msg: string; data: OrderPrintInfo[] }>(
        `/gw/phf/v2/order/history/r/print/info/phf?${qs.stringify(
            { ...params, token: Ids.getToken(), acctId: Ids.getAcctId() },
            { skipNulls: true },
        )}`,
    );
};

/**
 * 上报打印，接口会返回最新的打印次数
 */
const reportOrderPrint = (params: { orderViewId: string; wmPoiId: number; type: 0 | 1 }) => {
    return request.postAsFormSubmit<number>('/print/count/v2', {
        type: params.type, //0小票打印, 1标签打印
        wmOrderViewId: params.orderViewId,
        wmPoiId: params.wmPoiId,
    });
};

/** 请求订单里商品的标签列表，每个商品会打印一个标签，同一个商品买N份也会打印N个 */
const getOrderTagPrintInfo = (params: { orderViewId: string; wmPoiId: number }) => {
    return request.postAsFormSubmit<{
        takeFoodCodeList: OrderFoodTagPrintContent[];
        depositCode: DepositCodeInfo | null;
    }>('/gw/tc/b/grouporder/print/label', params);
};

/** 获取原始打印指令 */
const getRawTagPrintContent = (params: { orderViewId: string; wmPoiId: number }) => {
    return request.get<
        | { mode: 2; templateDataList: string[] }
        | {
              mode: 1;
              takeFoodCodeList: OrderFoodTagPrintContent[];
              depositCode: DepositCodeInfo | null;
          }
    >('/gw/tc/b/print/assemble_label', {
        params: {
            source: 1,
            ...params,
        },
    });
};

export {
    OrderPrintInfo,
    AdPrintContent,
    getAdPrintContent,
    getReceiptPrintConfig,
    getOrderPrintInfo,
    getPhfSingleOrderPrintInfo,
    reportOrderPrint,
    getOrderTagPrintInfo,
    OrderFoodTagPrintContent,
    getPhfCombineOrderPrintInfo,
    DepositCodeInfo,
    getRawTagPrintContent,
};
